import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  EMPTY_TABLE_QUERY_STATE,
  activeFilterCount,
  applyTableFilters,
  buildCsv,
  defaultExportColumns,
  downloadCsv,
  type TableQuerySpec,
  type TableQueryState,
} from "@/lib/tableQuery";

interface Options {
  /** Prefix for the address-bar keys, so two tables on one page don't clash. */
  urlKey?: string;
  pageSize?: number;
  /** Keep filters in the address bar (default true). */
  persistInUrl?: boolean;
}

export interface TableQueryApi {
  state: TableQueryState;
  /** Debounced text value shown in the input (immediate) vs state.search (debounced). */
  searchInput: string;
  setSearchInput: (value: string) => void;
  setFilter: (key: string, value: string) => void;
  setDateFrom: (value: string) => void;
  setDateTo: (value: string) => void;
  setAmountMin: (value: string) => void;
  setAmountMax: (value: string) => void;
  setSort: (key: string, direction?: "asc" | "desc") => void;
  toggleSort: (key: string) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  reset: () => void;
  activeCount: number;
  spec: TableQuerySpec;
}

export function useTableQuery(spec: TableQuerySpec, options: Options = {}): TableQueryApi {
  const { urlKey = "q", pageSize = 20, persistInUrl = true } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const p = (name: string) => `${urlKey}_${name}`;

  const initial = useMemo<TableQueryState>(() => {
    const read = (name: string, fallback = "") =>
      (persistInUrl ? searchParams.get(p(name)) : null) ?? fallback;
    const filters: Record<string, string> = {};
    for (const f of spec.filters ?? []) {
      filters[f.key] = read(`f_${f.key}`, "all");
    }
    return {
      ...EMPTY_TABLE_QUERY_STATE,
      search: read("s"),
      filters,
      dateFrom: read("from"),
      dateTo: read("to"),
      amountMin: read("min"),
      amountMax: read("max"),
      sortKey: read("sort", spec.defaultSort?.key ?? spec.dateColumn ?? ""),
      sortDirection: (read("dir", spec.defaultSort?.direction ?? "desc") as "asc" | "desc"),
      page: Number(read("p", "1")) || 1,
      pageSize,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [state, setState] = useState<TableQueryState>(initial);
  const [searchInput, setSearchInputRaw] = useState(initial.search);
  const debounceRef = useRef<number | null>(null);

  // Debounce the text box into the query state.
  const setSearchInput = useCallback((value: string) => {
    setSearchInputRaw(value);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setState((prev) => ({ ...prev, search: value, page: 1 }));
    }, 300);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
  }, []);

  // Mirror the state into the address bar so filters survive reloads and can be shared.
  useEffect(() => {
    if (!persistInUrl) return;
    const next = new URLSearchParams(window.location.search);
    const put = (name: string, value: string, empty = "") => {
      if (value && value !== empty) next.set(p(name), value);
      else next.delete(p(name));
    };
    put("s", state.search);
    for (const f of spec.filters ?? []) put(`f_${f.key}`, state.filters[f.key] ?? "all", "all");
    put("from", state.dateFrom);
    put("to", state.dateTo);
    put("min", state.amountMin);
    put("max", state.amountMax);
    put("sort", state.sortKey === (spec.defaultSort?.key ?? spec.dateColumn ?? "") ? "" : state.sortKey);
    put("dir", state.sortDirection === (spec.defaultSort?.direction ?? "desc") ? "" : state.sortDirection);
    put("p", state.page > 1 ? String(state.page) : "");
    if (next.toString() !== window.location.search.replace(/^\?/, "")) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, persistInUrl]);

  const setFilter = useCallback((key: string, value: string) => {
    setState((prev) => ({ ...prev, filters: { ...prev.filters, [key]: value }, page: 1 }));
  }, []);

  const api: TableQueryApi = {
    state,
    searchInput,
    setSearchInput,
    setFilter,
    setDateFrom: (value) => setState((prev) => ({ ...prev, dateFrom: value, page: 1 })),
    setDateTo: (value) => setState((prev) => ({ ...prev, dateTo: value, page: 1 })),
    setAmountMin: (value) => setState((prev) => ({ ...prev, amountMin: value, page: 1 })),
    setAmountMax: (value) => setState((prev) => ({ ...prev, amountMax: value, page: 1 })),
    setSort: (key, direction) =>
      setState((prev) => ({ ...prev, sortKey: key, sortDirection: direction ?? prev.sortDirection, page: 1 })),
    toggleSort: (key) =>
      setState((prev) =>
        prev.sortKey === key
          ? { ...prev, sortDirection: prev.sortDirection === "asc" ? "desc" : "asc", page: 1 }
          : { ...prev, sortKey: key, sortDirection: "desc", page: 1 },
      ),
    setPage: (page) => setState((prev) => ({ ...prev, page })),
    setPageSize: (size) => setState((prev) => ({ ...prev, pageSize: size, page: 1 })),
    reset: () => {
      setSearchInputRaw("");
      const filters: Record<string, string> = {};
      for (const f of spec.filters ?? []) filters[f.key] = "all";
      setState((prev) => ({
        ...EMPTY_TABLE_QUERY_STATE,
        filters,
        sortKey: spec.defaultSort?.key ?? spec.dateColumn ?? "",
        sortDirection: spec.defaultSort?.direction ?? "desc",
        pageSize: prev.pageSize,
      }));
    },
    activeCount: activeFilterCount(state),
    spec,
  };

  return api;
}

/** Local filtering + paging for lists that already hold all their rows. */
export function useLocalTableQuery<T extends Record<string, any>>(
  rows: T[] | undefined,
  spec: TableQuerySpec,
  options: Options = {},
) {
  const query = useTableQuery(spec, options);
  const all = rows ?? [];

  const filtered = useMemo(
    () => applyTableFilters(all, spec, query.state),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, query.state, spec],
  );

  const { page, pageSize } = query.state;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(
    () => filtered.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filtered, safePage, pageSize],
  );

  const from = filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, filtered.length);
  const showingText =
    filtered.length === 0 ? "No results" : `Showing ${from}–${to} of ${filtered.length}`;

  const exportCsv = useCallback(() => {
    const columns = defaultExportColumns(spec);
    downloadCsv(spec.exportFileName || "export", buildCsv(filtered, columns));
  }, [filtered, spec]);

  return {
    query,
    filtered,
    rows: paginated,
    page: safePage,
    totalPages,
    total: filtered.length,
    showingText,
    setPage: query.setPage,
    exportCsv,
  };
}
